'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';

const projects = [
  {
    id: 1,
    title: 'Platform Startup Digital',
    category: 'Web App · AI Integration',
    description: 'Dashboard analytics real-time dengan rekomendasi AI untuk startup fintech Jakarta. Dibangun dalam 3 minggu.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b09aca67-1772313899728.png',
    alt: 'Dark analytics dashboard with glowing charts and graphs',
    tag: 'Fintech',
    metric: '2× Revenue',
    colSpan: 'md:col-span-7',
    height: 'min-h-[300px] md:min-h-[380px]',
  },
  {
    id: 2,
    title: 'UMKM Digital Store',
    category: 'E-Commerce · Automation',
    description: 'Toko online dengan sistem auto-restock dan notifikasi WhatsApp otomatis untuk UMKM kuliner.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1cb6b7c0c-1776022491521.png',
    alt: 'Dark product display with warm accent lighting',
    tag: 'UMKM',
    metric: '3× Orders',
    colSpan: 'md:col-span-5',
    height: 'min-h-[300px] md:min-h-[380px]',
  },
  {
    id: 3,
    title: 'Korporat AI Portal',
    category: 'Enterprise · AI Chatbot',
    description: 'Portal internal korporasi dengan chatbot AI yang terintegrasi ke database HR dan knowledge base.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c0a9d673-1775460474459.png',
    alt: 'Dark corporate office environment with dim professional lighting',
    tag: 'Korporat',
    metric: '40% Efisiensi',
    colSpan: 'md:col-span-5',
    height: 'min-h-[300px] md:min-h-[360px]',
  },
  {
    id: 4,
    title: 'Automation Flow System',
    category: 'Workflow · Integration',
    description: 'Sistem otomasi end-to-end yang menghubungkan 12 platform berbeda — dari CRM ke email hingga laporan harian.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1af11dd27-1772223403256.png',
    alt: 'Dark server room with glowing cables and nodes',
    tag: 'Automation',
    metric: '80% Waktu Hemat',
    colSpan: 'md:col-span-7',
    height: 'min-h-[300px] md:min-h-[360px]',
  },
];

export default function PortfolioSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void };
    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        gsap.fromTo(titleRef.current, { opacity: 0, y: 32, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power2.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
        const cards = gridRef.current?.querySelectorAll('.portfolio-card');
        if (cards) gsap.fromTo(cards, { opacity: 0, y: 50, scale: 0.97 },
          { opacity: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.15, ease: 'power2.out', scrollTrigger: { trigger: gridRef.current, start: 'top 78%' } });
      }, sectionRef);
    };
    init();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section id="portfolio" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div ref={titleRef} className="opacity-0 mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-lime-500" />
              <span className="text-lime-500 text-[11px] font-semibold tracking-[0.2em] uppercase">Karya Kami</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-light text-white tracking-tight leading-[1.1]">
              <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Portfolio</span>{' '}Terpilih
            </h2>
            <p className="mt-3 text-zinc-400 text-base max-w-md leading-relaxed">
              Beberapa proyek yang kami banggakan — dari startup hingga korporasi.
            </p>
          </div>
          <a href="#kontak" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto">
            Diskusikan Proyek Anda <ArrowRight size={15} />
          </a>
        </div>

        {/* Portfolio bento grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {projects.map(project => (
            <div key={project.id} className={`${project.colSpan} portfolio-card opacity-0`}>
              <div className="spotlight-card rounded-2xl p-[1px] h-full" style={{ background: 'radial-gradient(circle, rgb(24,24,27) 0%, rgb(0,0,0) 100%)' }}>
                <div className={`rounded-[15px] bg-[rgba(15,15,18,0.9)] overflow-hidden h-full ${project.height} relative group cursor-pointer`}>
                  {/* Background image */}
                  <div className="absolute inset-0">
                    <Image
                      src={project.image}
                      alt={project.alt}
                      fill
                      className="object-cover opacity-50 group-hover:opacity-65 group-hover:scale-105 transition-all duration-700"
                      sizes="(max-width: 768px) 100vw, 60vw"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-7 md:p-8 flex flex-col justify-between h-full">
                    {/* Top row */}
                    <div className="flex items-start justify-between">
                      <span className="px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.12em] uppercase border border-lime-400/40 bg-lime-400/10 text-lime-400">
                        {project.tag}
                      </span>
                      <span className="px-3 py-1 rounded-full text-[11px] font-bold text-white border border-white/20 bg-white/10">
                        {project.metric}
                      </span>
                    </div>

                    {/* Bottom content */}
                    <div className="flex flex-col gap-2">
                      <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">
                        {project.category}
                      </span>
                      <h3 className="text-xl md:text-2xl font-semibold text-white tracking-tight leading-tight">
                        {project.title}
                      </h3>
                      <p className="text-zinc-400/80 text-[13px] leading-relaxed max-w-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-1">
                        {project.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
